/**
 * /hospitality/flash — GM-level daily property snapshot (P6).
 *
 * Gaming + rooms + F&B + retail in one unified view with department mix
 * percentages, gaming MTD, occupancy snapshot, and F&B covers.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, StatCard, Panel, money } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/flash")({
  component: DailyFlashPage,
});

// ── Types ──────────────────────────────────────────────────────────────────
interface DeptRevenue {
  net: string;
  pct_of_total: string;
}
interface GamingRevenue extends DeptRevenue {
  slot_win: string;
  table_win: string;
  gross: string;
  promos: string;
}
interface RoomsRevenue extends DeptRevenue {
  room_revenue: string;
  resort_fees: string;
  room_tax: string;
  promos: string;
}
interface FnbRevenue extends DeptRevenue {
  food: string;
  beverage: string;
  promos: string;
}
interface FlashRevenue {
  gaming: GamingRevenue | null;
  rooms: RoomsRevenue | null;
  fnb: FnbRevenue | null;
  retail: DeptRevenue | null;
  total: string;
}
interface DailyFlashResult {
  property: string;
  business_date: string | null;
  revenue: FlashRevenue | null;
  gaming_mtd: Record<string, string> | null;
  lodging: Record<string, string | number> | null;
  fnb: Record<string, string | number> | null;
  flags: string[];
  note: string | null;
  message?: string;
}

function DailyFlashPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-daily-flash"],
    queryFn: () => api<DailyFlashResult>("/api/hospitality/flash"),
  });

  const data = q.data;
  const rev = data?.revenue;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Daily Flash"
        subtitle={
          data
            ? `${data.property}${data.business_date ? ` · ${data.business_date}` : ""}`
            : "GM-level property snapshot"
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
          </div>
        )}

        {!q.isLoading && data && !data.message && rev && (
          <div className="space-y-4">
            {/* Flags */}
            {data.flags.length > 0 && (
              <div className="space-y-1">
                {data.flags.map((f, i) => (
                  <div key={i} className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded px-3 py-1.5">
                    {f}
                  </div>
                ))}
              </div>
            )}

            {/* Total revenue summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard label="Total revenue" value={money(rev.total, 0)} />
              <StatCard label="Gaming" value={money(rev.gaming?.net ?? null, 0)} />
              <StatCard label="Rooms" value={money(rev.rooms?.net ?? null, 0)} />
              <StatCard label="F&B" value={money(rev.fnb?.net ?? null, 0)} />
            </div>

            {/* Department mix */}
            <Panel label="Revenue by department">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Department</th>
                    <th className="text-right py-1.5 px-3">Mix</th>
                    <th className="text-right py-1.5 pl-3">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {rev.gaming && (
                    <tr className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3">
                        <div className="text-slate-800 dark:text-slate-200">Gaming</div>
                        <div className="text-xs text-slate-400">
                          Slots {money(rev.gaming.slot_win, 0)} · Tables {money(rev.gaming.table_win, 0)} · Promos −{money(rev.gaming.promos, 0)}
                        </div>
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {rev.gaming.pct_of_total}
                      </td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(rev.gaming.net, 0)}
                      </td>
                    </tr>
                  )}
                  {rev.rooms && (
                    <tr className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3">
                        <div className="text-slate-800 dark:text-slate-200">Rooms</div>
                        <div className="text-xs text-slate-400">
                          Room revenue {money(rev.rooms.room_revenue, 0)} · Resort fees {money(rev.rooms.resort_fees, 0)} · Room tax {money(rev.rooms.room_tax, 0)}
                        </div>
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {rev.rooms.pct_of_total}
                      </td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(rev.rooms.net, 0)}
                      </td>
                    </tr>
                  )}
                  {rev.fnb && (
                    <tr className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3">
                        <div className="text-slate-800 dark:text-slate-200">Food & Beverage</div>
                        <div className="text-xs text-slate-400">
                          Food {money(rev.fnb.food, 0)} · Bev {money(rev.fnb.beverage, 0)}
                        </div>
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {rev.fnb.pct_of_total}
                      </td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(rev.fnb.net, 0)}
                      </td>
                    </tr>
                  )}
                  {rev.retail && (
                    <tr className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3 text-slate-800 dark:text-slate-200">Retail</td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {rev.retail.pct_of_total}
                      </td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(rev.retail.net, 0)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t-2 border-slate-300 dark:border-slate-600 font-semibold">
                    <td className="py-1.5 pr-3 text-xs text-slate-700 dark:text-slate-200">Total</td>
                    <td className="py-1.5 px-3 text-right text-xs text-slate-500">100%</td>
                    <td className="py-1.5 pl-3 text-right tabular-nums text-xs text-slate-900 dark:text-slate-100">
                      {money(rev.total, 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Panel>

            {/* Operational metrics row */}
            {(data.gaming_mtd || data.lodging || data.fnb) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {data.gaming_mtd && (
                  <Panel label="Gaming MTD">
                    <div className="space-y-2">
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Through prior day</div>
                        <div className="text-sm font-semibold">{money(data.gaming_mtd.through_prior, 0)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Including today</div>
                        <div className="text-sm font-semibold">{money(data.gaming_mtd.including_today, 0)}</div>
                      </div>
                    </div>
                  </Panel>
                )}
                {data.lodging && (
                  <Panel label="Rooms snapshot">
                    <div className="space-y-2">
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Occupancy</div>
                        <div className="text-sm font-semibold">{data.lodging.occupancy_pct}%</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">ADR</div>
                        <div className="text-sm font-semibold">{money(String(data.lodging.adr))}</div>
                      </div>
                    </div>
                  </Panel>
                )}
                {data.fnb && (
                  <Panel label="F&B snapshot">
                    <div>
                      <div className="text-[10px] uppercase text-slate-400 mb-0.5">Total covers</div>
                      <div className="text-sm font-semibold">
                        {Number(data.fnb.total_covers).toLocaleString()}
                      </div>
                    </div>
                  </Panel>
                )}
              </div>
            )}

            {data.note && (
              <div className="text-xs text-slate-400 dark:text-slate-500 pt-1">{data.note}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
